/* global rwgps */
import { onMapboxReady } from "lib/mapboxUtils";
import { uuid } from "lib/utils";
import _ from "lodash";
import React from "react";

const dasharray = (group) => {
  switch (group) {
    case "unpaved":
      return [1, 3];
    case "unknown":
      return [1, 0];
    default:
      console.warn(`unexpected surface group ${group}`);
  }
};

export class MapboxPolyline extends React.PureComponent {
  constructor(props) {
    super(props);
    const dataSlices = this.sliceOnSurface();
    this.state = {
      added: false,
      dataSlices,
    };
    this._id = uuid();
    if (dataSlices) {
      this._ids = dataSlices.map(() => uuid()); // uuid for each slice
    }
  }

  sliceOnSurface = () => {
    const { positions } = this.props;
    const surfaceGroups = rwgps?.surfaces?.surfaceGroups;
    if (!surfaceGroups || !_.some(positions, (p) => p.surface)) {
      return [];
    }
    let lastSurface;
    let lastIdx = 0;
    let lastSurfaceGroup;
    let thisSurfaceGroup;
    const slices = [];
    positions.forEach((p, idx) => {
      thisSurfaceGroup = surfaceGroups[p.surface];
      if (lastSurface !== undefined && lastSurfaceGroup !== thisSurfaceGroup) {
        if (surfaceGroups[lastSurface] !== "paved") {
          slices.push({
            positions: positions.slice(lastIdx, idx),
            surfaceGroup: lastSurfaceGroup ?? "unknown",
          });
        }
        lastIdx = idx;
      }
      lastSurface = p.surface;
      lastSurfaceGroup = surfaceGroups[lastSurface];
    });
    if (lastSurfaceGroup !== "paved") {
      // push the remaining positions
      slices.push({
        positions: positions.slice(lastIdx),
        surfaceGroup: lastSurfaceGroup ?? "unknown",
      });
    }
    return slices;
  };

  componentDidMount() {
    const { mapInstance } = this.props;
    onMapboxReady(mapInstance, this.addToMap); // I think onMapboxReady might be unreliable
  }

  componentDidUpdate(prevProps) {
    // we're using an `added` flag instead of onMapboxReady
    // here for performance reasons, but it accomplishes the same thing
    const { mapInstance, positions, showSurface } = this.props;
    const { added } = this.state;
    if (!added) {
      return;
    }

    if (prevProps.positions.length !== positions.length) {
      // if positions change, maybe so do surface boundaries
      this.readdToMap();
    }

    if (prevProps.showSurface !== showSurface) {
      this._ids.forEach((id) => mapInstance.setLayoutProperty(id, "visibility", showSurface ? "visible" : "none"));
    }
  }

  componentWillUnmount() {
    this.removeFromMap();
  }

  readdToMap = () => {
    this.removeFromMap();
    this.setState({ dataSlices: this.sliceOnSurface() }, () => {
      this._id = uuid();
      this._ids = this.state.dataSlices.map(() => uuid());
      this.addToMap();
    });
  };

  removeFromMap = () => {
    const { mapInstance } = this.props;
    mapInstance.removeLayer(this._id);
    this._ids.map((id) => mapInstance.removeLayer(id));
  };

  geoData = (positionsSlice) => {
    return {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: positionsSlice.map((point) => [point.lng, point.lat]),
      },
    };
  };

  addToMap = () => {
    const { mapInstance, positions, color, weight, onMouseEnter, onMouseLeave, onClick, showSurface } = this.props;
    mapInstance.addLayer({
      id: this._id,
      type: "line",
      source: {
        type: "geojson",
        data: this.geoData(positions),
      },
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": color,
        "line-width": weight,
      },
    });

    // even though we have no sticky pref for st for now, we need to account for
    // 1. google overlay, 2. uncheck surface, 3. swap to mapbox overlay
    this.state.dataSlices.forEach(({ positions, surfaceGroup }, idx) => {
      mapInstance.addLayer({
        id: this._ids[idx],
        type: "line",
        source: {
          type: "geojson",
          data: this.geoData(positions),
        },
        layout: {
          "line-join": "round",
          "line-cap": "round",
          visibility: showSurface ? "visible" : "none",
        },
        paint: {
          "line-color": "#FFFFFF",
          "line-width": weight * 0.75,
          "line-dasharray": dasharray(surfaceGroup),
        },
      });
    });

    onMouseEnter &&
      mapInstance.on("mouseenter", this._id, () => {
        mapInstance.getCanvas().style.cursor = "pointer";
        onMouseEnter(this._id);
      });
    onMouseLeave &&
      mapInstance.on("mouseleave", this._id, () => {
        mapInstance.getCanvas().style.cursor = "";
        onMouseLeave();
      });
    onClick && mapInstance.on("click", this._id, () => onClick());

    this.setState({ added: true });
  };

  render() {
    return null;
  }
}
